import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Calendar, Clock, MapPin, User, Briefcase, Users, Trash2, Pencil } from 'lucide-react';
import { getDateFnsLocale } from '@/lib/utils/date';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { CalendarEvent, DisplayEvent } from '../types';

interface EventDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: CalendarEvent | DisplayEvent | null;
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  canEdit?: boolean | undefined;
  canDelete?: boolean | undefined;
}

export function EventDetailsDialog({
  open,
  onOpenChange,
  event,
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
}: EventDetailsDialogProps) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  if (!event) return null;

  const startDate = 'startsAt' in event
    ? parseISO(event.startsAt)
    : parseISO(event.startDate);
  const endDate = 'endsAt' in event
    ? parseISO(event.endsAt)
    : parseISO(event.endDate);

  const getEventColor = (color?: string) => {
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      red: 'bg-red-500',
      yellow: 'bg-yellow-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
    };
    return colorMap[color ?? 'blue'] ?? 'bg-blue-500';
  };

  const eventColor = 'type' in event ? event.type?.color : ('color' in event ? event.color : undefined);
  const location = 'location' in event ? event.location : undefined;
  const matter = 'matter' in event ? event.matter : undefined;
  const client = 'client' in event ? event.client : undefined;
  const attendees = 'attendees' in event ? event.attendees : undefined;
  const user = 'user' in event ? event.user : ('createdBy' in event ? event.createdBy : undefined);
  const target = 'target' in event ? event.target : undefined;

  // Get target color
  const getTargetColor = (targetType?: string) => {
    switch (targetType) {
      case 'FIRM_EVENT':
        return 'bg-[#219ebc]';
      case 'NEW_CLIENT':
        return 'bg-[#fca311]';
      case 'CASE_MEETING':
        return 'bg-[#2b9348]';
      default:
        return 'bg-blue-500';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${target ? getTargetColor(target) : getEventColor(eventColor)}`} />
              <DialogTitle className="text-lg">{event.description || event.title}</DialogTitle>
            </div>
            <div className="flex gap-1">
              {canEdit && onEdit && (
                <Button size="icon" variant="ghost" onClick={onEdit}>
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {canDelete && onDelete && (
                <Button size="icon" variant="ghost" onClick={onDelete}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Date & Time */}
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <div className="font-medium">
                {format(startDate, 'EEEE, MMMM d, yyyy', { locale: dateFnsLocale })}
              </div>
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {format(startDate, 'HH:mm', { locale: dateFnsLocale })} - {format(endDate, 'HH:mm', { locale: dateFnsLocale })}
              </div>
            </div>
          </div>

          {/* Target Type */}
          {target && (
            <>
              <Separator />
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className={`${getTargetColor(target)} text-white`}
                >
                  {t(`target.${target}`)}
                </Badge>
              </div>
            </>
          )}

          {/* Location */}
          {location && (
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="text-sm">{location}</div>
            </div>
          )}

          {/* Matter */}
          {matter && (
            <div className="flex items-start gap-3">
              <Briefcase className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-sm font-medium">{t('matter')}</div>
                <div className="text-sm text-muted-foreground">
                  {matter.name ?? matter.caseNumber}
                </div>
              </div>
            </div>
          )}

          {/* Client */}
          {client && (
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-sm font-medium">{t('client')}</div>
                <div className="text-sm text-muted-foreground">{client.name}</div>
              </div>
            </div>
          )}

          {/* Attendees */}
          {attendees && attendees.length > 0 && (
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-sm font-medium mb-1">{t('attendees')}</div>
                <div className="flex flex-wrap gap-1">
                  {attendees.map((attendee) => (
                    <Badge key={attendee.id} variant="secondary">
                      {attendee.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Created By */}
          {user && (
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <User className="h-4 w-4 mt-0.5" />
              <div>
                {t('createdBy')}: {user.name}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
